import { CLIENT_SIZES, clientSizeLabel } from 'constants/clientSizes';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useEffect, useState } from 'react';
import ApiDatabase from 'server';
import crmApi from 'server/crm/crmApi';
import {
  MONTHS,
  etpPrice,
  expectedBy,
  rowPlan,
  territoryPlan,
} from './agencyGoals';
import StatsCard, { CardAside } from './StatsCard';
import { euros } from './statsFormat';

// A bar of what was done against the year's goal, a tick where the plan
// expects it by now: green ahead of the plan, orange behind.
const Progress = ({ done, goal, expected }) => (
  <div className="relative h-2 w-24 shrink-0 overflow-hidden rounded bg-gray-100">
    <div
      className={`h-full rounded ${done >= expected ? 'bg-green-500' : 'bg-yellow-500'}`}
      style={{ width: `${goal ? Math.min(100, (done / goal) * 100) : 0}%` }}
    />
    <span
      className="absolute inset-y-0 w-px bg-gray-900"
      style={{ left: `${goal ? Math.min(100, (expected / goal) * 100) : 0}%` }}
    />
  </div>
);

const Cell = ({ done, goal, expected, format = String }) => (
  <td className="px-2 py-2.5">
    <div className="flex items-center justify-end gap-3">
      <span className="whitespace-nowrap text-gray-700">
        {format(done)} <span className="text-gray-400">/ {format(goal)}</span>
      </span>
      <Progress done={done} goal={goal} expected={expected} />
    </div>
  </td>
);

const money = (value) => euros(Math.round(value));
const sum = (values) => values.reduce((total, value) => total + value, 0);

/**
 * « Objectifs de l'agence » of the statistics, for the agency manager: by
 * territory and client size, the clients signed during `year` and the revenue
 * they made, against the plan of the admin console; the tick on each bar is
 * where the plan expects them by now.
 */
const AgencyGoalsCard = ({ year }) => {
  const { text } = useText('agencyGoals');
  const { language } = UseContext();
  const { call, ready } = useCrmContext();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!ready) return undefined;
    let alive = true;
    setData(null);
    const smic = new Promise((resolve) =>
      ApiDatabase.getMinimumWage(
        (wage) => resolve(Number(wage?.hourly?.gross) || 0),
        () => resolve(0),
      ),
    );
    Promise.all([
      call(crmApi.getAgencyGoals, { year }),
      call(crmApi.getAgencyGoalsProgress, { year }),
      smic,
    ]).then(
      ([goals, progress, hourly]) =>
        alive &&
        setData({
          goals,
          progress,
          price: etpPrice(hourly, goals.coefficient),
        }),
      () => alive && setData({ failed: true }),
    );
    return () => {
      alive = false;
    };
  }, [call, ready, year]);

  const title = text('cardTitle', { year });
  if (!data)
    return (
      <StatsCard title={title}>
        <div className="h-24 animate-pulse rounded-lg bg-gray-100" />
      </StatsCard>
    );
  if (data.failed)
    return (
      <StatsCard title={title}>
        <p className="text-sm text-gray-600">{text('loadError')}</p>
      </StatsCard>
    );
  const { goals, progress, price } = data;
  const countsOf = (territory, size) =>
    goals.plan[territory]?.[size] ?? Array(MONTHS).fill(0);
  const territories = goals.territories.filter(
    (territory) => territoryPlan(goals, territory, price).clients > 0,
  );
  if (!territories.length)
    return (
      <StatsCard title={title}>
        <p className="text-sm text-gray-600">{text('cardEmpty', { year })}</p>
      </StatsCard>
    );
  const doneOf = (territory, size) =>
    progress[territory]?.[size] ?? { clients: 0, revenue: 0 };
  return (
    <StatsCard title={title} aside={<CardAside>{text('cardAside')}</CardAside>}>
      <div className="-mx-5 overflow-x-auto">
        <table className="w-full min-w-[620px] text-sm">
          <thead>
            <tr className="border-y border-gray-100 bg-[#fcfcfc] text-left text-xs font-medium uppercase tracking-[0.2px] text-gray-500">
              <th className="py-2.5 pl-5 pr-2 font-medium">
                {text('colTerritory')}
              </th>
              <th className="px-2 py-2.5 text-right font-medium">
                {text('colSigned')}
              </th>
              <th className="py-2.5 pl-2 pr-5 text-right font-medium">
                {text('colRevenueDone')}
              </th>
            </tr>
          </thead>
          {territories.map((territory) => {
            const sizes = CLIENT_SIZES.filter(
              (size) => sum(countsOf(territory, size)) > 0,
            );
            const rows = sizes.map((size) => {
              const plan = rowPlan(goals, territory, size, price);
              return {
                size,
                plan,
                done: doneOf(territory, size),
                expected: expectedBy(plan, countsOf(territory, size), year),
              };
            });
            const add = (pick) => sum(rows.map(pick));
            return (
              <tbody
                key={territory}
                className="border-b border-gray-100 last:border-0"
              >
                <tr>
                  <td className="py-2.5 pl-5 pr-2 font-semibold text-gray-900">
                    {territory}
                  </td>
                  <Cell
                    done={add((row) => row.done.clients)}
                    goal={add((row) => row.plan.clients)}
                    expected={add((row) => row.expected.clients)}
                  />
                  <Cell
                    done={add((row) => row.done.revenue)}
                    goal={add((row) => row.plan.revenue)}
                    expected={add((row) => row.expected.revenue)}
                    format={money}
                  />
                </tr>
                {rows.map((row) => (
                  <tr key={row.size}>
                    <td className="py-2 pl-9 pr-2 text-gray-600">
                      {clientSizeLabel(language, row.size)}
                    </td>
                    <Cell
                      done={row.done.clients}
                      goal={row.plan.clients}
                      expected={row.expected.clients}
                    />
                    <Cell
                      done={row.done.revenue}
                      goal={row.plan.revenue}
                      expected={row.expected.revenue}
                      format={money}
                    />
                  </tr>
                ))}
              </tbody>
            );
          })}
        </table>
      </div>
    </StatsCard>
  );
};

export default AgencyGoalsCard;
